import { useId } from 'react';

import type { GraphView, Tone } from '../types';
import { CANVAS, GEO, toneColor, toneFill } from '../theme';

import { Caption, EmptyCanvas, FONT, LAYOUT, fmt, frameProps, nodeRadiusFor } from './shared';

const R = GEO.nodeRadius;
const PAD = GEO.padding;
const RING_SPACING = 70;
const BADGE_ROOM = 56;
/** Perpendicular separation applied to each half of a reciprocal edge pair. */
const RECIPROCAL_OFFSET = 7;

interface Point {
  x: number;
  y: number;
}

/**
 * Longest-path layering by Kahn's algorithm. A cycle leaves nodes unprocessed,
 * and rather than looping forever (or drawing a lie) we report failure so the
 * caller falls back to the circle layout.
 */
function layeredDepths(
  ids: string[],
  edges: Array<{ from: string; to: string }>,
): Map<string, number> | null {
  const indeg = new Map(ids.map((id) => [id, 0]));
  const out = new Map<string, string[]>(ids.map((id) => [id, []]));
  for (const edge of edges) {
    if (edge.from === edge.to) continue;
    if (!indeg.has(edge.from) || !indeg.has(edge.to)) continue;
    out.get(edge.from)!.push(edge.to);
    indeg.set(edge.to, indeg.get(edge.to)! + 1);
  }
  const depth = new Map(ids.map((id) => [id, 0]));
  const queue = ids.filter((id) => indeg.get(id) === 0);
  let processed = 0;
  while (queue.length > 0) {
    const id = queue.shift()!;
    processed += 1;
    for (const next of out.get(id)!) {
      depth.set(next, Math.max(depth.get(next)!, depth.get(id)! + 1));
      indeg.set(next, indeg.get(next)! - 1);
      if (indeg.get(next) === 0) queue.push(next);
    }
  }
  return processed === ids.length ? depth : null;
}

export function GraphRenderer({ view }: { view: GraphView }) {
  const rawId = useId();
  const base = `gr-${rawId.replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const nodes = view.nodes;
  const title =
    view.caption ??
    (nodes.length === 0 ? 'Empty graph' : `Graph of ${nodes.length} nodes and ${view.edges.length} edges`);

  if (nodes.length === 0) return <EmptyCanvas title={title} />;

  const ids = nodes.map((node) => node.id);
  const known = new Set(ids);
  const edges = view.edges.filter((edge) => known.has(edge.from) && known.has(edge.to));

  const marks = new Map<string, { tone: Tone; badge?: string }>();
  for (const mark of view.marks ?? []) {
    if (known.has(mark.nodeId)) marks.set(mark.nodeId, { tone: mark.tone, badge: mark.badge });
  }
  const hasBadges = [...marks.values()].some((m) => !!m.badge);

  /*
   * Node text is drawn centred inside its circle, so a label longer than the
   * base radius allows (a model name, a full sentence-fragment) would spill
   * past its own outline into whatever sits next to it. Grow every node in
   * this graph to the widest label present, uniformly, so the spacing maths
   * below (which assumes one shared radius) still keeps neighbours clear.
   * Short DSA-style labels never exceed the base radius, so this is a no-op
   * for existing content.
   */
  const NR = Math.max(R, ...nodes.map((node) => nodeRadiusFor(fmt(node.label ?? node.id), R)));
  const colGap = 2 * NR + 32;

  const depths = view.layout === 'layered' ? layeredDepths(ids, edges) : null;
  const captionY = view.caption ? PAD + 8 : 0;
  const top = (view.caption ? PAD + LAYOUT.captionBand : PAD) + NR;

  let width: number;
  let height: number;
  const pos = new Map<string, Point>();

  if (depths) {
    const byDepth = new Map<number, string[]>();
    for (const id of ids) {
      const d = depths.get(id)!;
      byDepth.set(d, [...(byDepth.get(d) ?? []), id]);
    }
    const maxDepth = Math.max(...byDepth.keys());
    const widest = Math.max(...[...byDepth.values()].map((row) => row.length));
    const contentW = (widest - 1) * colGap + 2 * NR;
    /*
     * A parent directly above its child sits `levelGap` away vertically with
     * no horizontal offset at all — the fixed base gap (tuned for a 24px
     * radius) is shorter than the diameter of an enlarged node, so a parent
     * and child would overlap even though colGap already keeps same-depth
     * siblings clear. Grow the vertical step the same way.
     */
    const levelGap = Math.max(GEO.levelGap, 2 * NR + 20);
    width = PAD * 2 + contentW + (hasBadges ? BADGE_ROOM : 0);
    height = top + maxDepth * levelGap + NR + PAD;
    const centerX = PAD + contentW / 2;
    for (const [d, row] of byDepth) {
      row.forEach((id, i) => {
        pos.set(id, {
          x: centerX - ((row.length - 1) * colGap) / 2 + i * colGap,
          y: top + d * levelGap,
        });
      });
    }
  } else {
    /*
     * The ring radius from node count alone assumes small, uniform circles —
     * with only a few nodes it can come out smaller than the (now possibly
     * much larger) node radius calls for, which would seat adjacent nodes'
     * circles on top of each other. The chord between two nodes spaced
     * 2π/n apart on a ring of this radius is `2 * radius * sin(π/n)`; solving
     * that for "at least 2*NR apart" gives the floor below.
     */
    const minRadiusForNodeSize = nodes.length <= 1 ? 0 : (NR + 6) / Math.sin(Math.PI / nodes.length);
    const radius =
      nodes.length === 1
        ? 0
        : Math.max(RING_SPACING, (nodes.length * RING_SPACING) / (2 * Math.PI), minRadiusForNodeSize);
    const span = 2 * (radius + NR);
    width = PAD * 2 + span + (hasBadges ? BADGE_ROOM : 0);
    height = top + span - NR + PAD;
    const centerX = PAD + radius + NR;
    const centerY = top + radius;
    ids.forEach((id, i) => {
      const angle = -Math.PI / 2 + (i * 2 * Math.PI) / nodes.length;
      pos.set(id, { x: centerX + radius * Math.cos(angle), y: centerY + radius * Math.sin(angle) });
    });
  }

  const arrowTones = [...new Set(edges.filter((e) => e.directed).map((e) => e.tone ?? 'muted'))];
  const arrowId = (tone: Tone | undefined) => `${base}-arrow-${tone ?? 'muted'}`;

  /** `"from to"` keys, so an edge can tell whether its reverse is also drawn. */
  const edgeKeys = new Set(edges.map((e) => `${e.from} ${e.to}`));
  /** Node centres a weight chip must not be placed on top of. */
  const obstacles = [...pos.entries()].map(([id, p]) => ({ id, x: p.x, y: p.y }));

  return (
    <svg {...frameProps(width, height, view.caption)}>
      <title>{title}</title>
      <defs aria-hidden="true">
        {arrowTones.map((tone) => (
          <marker
            key={tone}
            id={arrowId(tone)}
            markerWidth={8}
            markerHeight={8}
            refX={7}
            refY={4}
            orient="auto"
            markerUnits="strokeWidth"
          >
            <path d="M 0 0 L 8 4 L 0 8 z" fill={tone === 'muted' ? CANVAS.textDim : toneColor(tone)} />
          </marker>
        ))}
      </defs>

      {view.caption ? <Caption text={view.caption} x={PAD} y={captionY} /> : null}

      <g aria-hidden="true">
        {edges.map((edge, k) => {
          const a = pos.get(edge.from)!;
          const b = pos.get(edge.to)!;
          /*
           * A residual graph is full of reciprocal pairs (u→v and v→u), which
           * would otherwise be drawn as the same segment with both weight chips
           * stacked at the identical midpoint. Nudge each of the pair to its own
           * side of the line so both the edge and its capacity stay readable.
           */
          const reciprocal = edgeKeys.has(`${edge.to} ${edge.from}`);
          const side = reciprocal ? (edge.from < edge.to ? 1 : -1) : 0;
          const stroke = edge.tone ? toneColor(edge.tone) : CANVAS.cellBorder;
          const marker = edge.directed ? `url(#${arrowId(edge.tone ?? 'muted')})` : undefined;

          if (edge.from === edge.to) {
            // Self loop: a small circle riding on top of the node, no maths that
            // can divide by a zero-length vector.
            return (
              <path
                key={`edge-${k}`}
                d={`M ${a.x - 9} ${a.y - NR + 3} A 15 15 0 1 1 ${a.x + 9} ${a.y - NR + 3}`}
                fill="none"
                stroke={stroke}
                strokeWidth={1.5}
              />
            );
          }

          const dx = b.x - a.x;
          const dy = b.y - a.y;
          const dist = Math.hypot(dx, dy) || 1;
          const ux = dx / dist;
          const uy = dy / dist;
          // Unit normal, used both to separate reciprocal pairs and to lift a
          // weight chip clear of a node it would otherwise sit on top of.
          const nx = -uy;
          const ny = ux;
          const off = side * RECIPROCAL_OFFSET;
          const x1 = a.x + ux * NR + nx * off;
          const y1 = a.y + uy * NR + ny * off;
          const x2 = b.x - ux * (NR + 3) + nx * off;
          const y2 = b.y - uy * (NR + 3) + ny * off;
          const label = edge.weight === undefined ? null : String(edge.weight);
          const chipW = label ? label.length * 7 + 10 : 0;

          /*
           * In a circle layout an edge between positions i and i+2 passes
           * straight through position i+1, so the raw midpoint can land on an
           * unrelated node. Try a few points along the edge and lift clear on
           * the normal — geometry is the renderer's job, not the author's, so
           * chip readability must not depend on node ordering in the JSON.
           */
          let chip = { x: (x1 + x2) / 2, y: (y1 + y2) / 2 };
          if (label) {
            const others = obstacles.filter((o) => o.id !== edge.from && o.id !== edge.to);
            const clear = (p: { x: number; y: number }) =>
              others.every((o) => Math.hypot(o.x - p.x, o.y - p.y) > NR + 12);
            const at = (t: number, lift: number) => ({
              x: x1 + (x2 - x1) * t + nx * lift,
              y: y1 + (y2 - y1) * t + ny * lift,
            });
            const candidates = [
              chip,
              at(0.5, 14 * (side || 1)),
              at(0.32, 0),
              at(0.68, 0),
              at(0.32, 14 * (side || 1)),
            ];
            chip = candidates.find(clear) ?? chip;
          }

          return (
            <g key={`edge-${k}`}>
              <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={stroke} strokeWidth={1.5} markerEnd={marker} />
              {label ? (
                <>
                  <rect
                    x={chip.x - chipW / 2}
                    y={chip.y - 8}
                    width={chipW}
                    height={16}
                    rx={5}
                    fill={CANVAS.panel}
                    stroke={CANVAS.panelBorder}
                  />
                  <text
                    x={chip.x}
                    y={chip.y}
                    fill={CANVAS.textDim}
                    fontSize={FONT.label}
                    textAnchor="middle"
                    dominantBaseline="central"
                  >
                    {label}
                  </text>
                </>
              ) : null}
            </g>
          );
        })}
      </g>

      {nodes.map((node) => {
        const p = pos.get(node.id)!;
        const mark = marks.get(node.id);
        const badge = mark?.badge;
        const badgeW = badge ? badge.length * 7 + 12 : 0;
        return (
          <g key={node.id}>
            <circle
              cx={p.x}
              cy={p.y}
              r={NR}
              fill={mark ? toneFill(mark.tone) : CANVAS.cell}
              stroke={mark ? toneColor(mark.tone) : CANVAS.cellBorder}
              strokeWidth={1.5}
            />
            <text
              x={p.x}
              y={p.y}
              fill={CANVAS.text}
              fontSize={FONT.value}
              textAnchor="middle"
              dominantBaseline="central"
            >
              {fmt(node.label ?? node.id)}
            </text>
            {badge ? (
              <>
                <rect
                  x={p.x + NR + 4}
                  y={p.y - 9}
                  width={badgeW}
                  height={18}
                  rx={9}
                  fill={toneFill(mark!.tone)}
                  stroke={toneColor(mark!.tone)}
                  aria-hidden="true"
                />
                <text
                  x={p.x + NR + 4 + badgeW / 2}
                  y={p.y}
                  fill={toneColor(mark!.tone)}
                  fontSize={FONT.label}
                  textAnchor="middle"
                  dominantBaseline="central"
                >
                  {badge}
                </text>
              </>
            ) : null}
          </g>
        );
      })}
    </svg>
  );
}

export default GraphRenderer;
